import {
  Opportunity,
  Requirement,
  ProposalMilestone,
  ProposalAddendum,
  RequiredForm,
  FormatRule,
  ProposalSectionGroup,
  StrategicAssessment,
} from "../types";

/**
 * Escapes a single CSV field value according to RFC 4180
 */
export function escapeCsv(value: unknown): string {
  if (value === null || value === undefined) return '""';
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return `"${str}"`;
}

/**
 * Generates an RFC 4180 CSV for the Requirements Compliance Matrix
 */
export function exportRequirementsMatrixCsv(
  _opportunity: Opportunity,
  requirements: Requirement[],
): string {
  const headers = [
    "Item #",
    "Section Ref",
    "Requirement Title",
    "RFP Prompt",
    "Category",
    "Mandatory",
    "Status",
    "Confirmed",
    "Assigned Drafter",
    "Drafter Email",
    "Addenda Impact",
    "Source Page",
  ];

  const rows = requirements.map((req, idx) => {
    return [
      escapeCsv(idx + 1),
      escapeCsv(req.sectionRef || ""),
      escapeCsv(req.title),
      escapeCsv(req.prompt),
      escapeCsv(req.category.toUpperCase()),
      escapeCsv(req.mandatory ? "YES" : "NO"),
      escapeCsv(req.status.replace(/_/g, " ").toUpperCase()),
      escapeCsv(req.confirmed ? "YES" : "NO"),
      escapeCsv(req.assignedTo || "Unassigned"),
      escapeCsv(req.assignedToEmail || ""),
      escapeCsv(
        req.addendumTags && req.addendumTags.length > 0 ? req.addendumTags.join("; ") : "None",
      ),
      escapeCsv(req.sourcePage || ""),
    ].join(",");
  });

  return [headers.join(","), ...rows].join("\r\n");
}

/**
 * Generates an RFC 4180 CSV for the Response Schedule & Milestones
 */
export function exportResponseScheduleCsv(
  _opportunity: Opportunity,
  milestones: ProposalMilestone[],
): string {
  const headers = [
    "Milestone Key",
    "Title",
    "Category",
    "Target Date",
    "Status",
    "Owner",
    "Timezone",
    "Description",
  ];

  const rows = milestones.map((m) => {
    return [
      escapeCsv(m.autoKey || "custom"),
      escapeCsv(m.title),
      escapeCsv(m.category.replace(/_/g, " ").toUpperCase()),
      escapeCsv(m.targetDate + (m.targetTime ? ` ${m.targetTime}` : "")),
      escapeCsv(m.status.toUpperCase()),
      escapeCsv(m.ownerName || m.ownerEmail || "Unassigned"),
      escapeCsv(m.timezone || "America/Chicago"),
      escapeCsv(m.description || ""),
    ].join(",");
  });

  return [headers.join(","), ...rows].join("\r\n");
}

/**
 * Generates an RFC 4180 CSV for the Bid Section Ownership Outline
 */
export function exportSectionAllocationCsv(
  _opportunity: Opportunity,
  sectionGroups: ProposalSectionGroup[],
): string {
  const headers = [
    "Section Ref",
    "Title",
    "Category",
    "Primary Drafter",
    "Drafter Email",
    "SME Reviewer",
    "Reviewer Email",
    "Target Words",
    "Actual Words",
    "Linked Requirements Count",
    "Status",
    "Due Date",
    "Notes",
  ];

  const rows = sectionGroups.map((s) => {
    return [
      escapeCsv(s.sectionRef),
      escapeCsv(s.title),
      escapeCsv(s.category.toUpperCase()),
      escapeCsv(s.assignedTo || "Unassigned"),
      escapeCsv(s.assignedToEmail || ""),
      escapeCsv(s.reviewerName || "Unassigned"),
      escapeCsv(s.reviewerEmail || ""),
      escapeCsv(s.targetWordCount || 0),
      escapeCsv(s.actualWordCount || 0),
      escapeCsv(s.requirements.length),
      escapeCsv(s.status.toUpperCase()),
      escapeCsv(s.dueDate || ""),
      escapeCsv(s.notes || ""),
    ].join(",");
  });

  return [headers.join(","), ...rows].join("\r\n");
}

/**
 * Generates an RFC 4180 CSV for Required Forms & Format Rules
 */
export function exportFormsAndRulesCsv(
  _opportunity: Opportunity,
  forms: RequiredForm[],
  rules: FormatRule[],
): string {
  const headers = [
    "Compliance Item Type",
    "Name / Rule",
    "Detail / Description",
    "Mandatory / Enforced",
    "Status / Compliant",
    "Assigned To",
    "Source Page",
  ];

  const formRows = forms.map((f) => {
    return [
      escapeCsv("REQUIRED FORM"),
      escapeCsv(f.formName),
      escapeCsv(f.description || ""),
      escapeCsv(f.mandatory ? "YES" : "NO"),
      escapeCsv(f.status.replace(/_/g, " ").toUpperCase()),
      escapeCsv(f.assignedTo || "Unassigned"),
      escapeCsv(f.sourcePage || ""),
    ].join(",");
  });

  const ruleRows = rules.map((r) => {
    return [
      escapeCsv(`FORMAT RULE (${r.ruleType.replace(/_/g, " ").toUpperCase()})`),
      escapeCsv(r.requirement),
      escapeCsv(`Rule Type: ${r.ruleType}`),
      escapeCsv("YES"),
      escapeCsv(r.isCompliant ? "COMPLIANT" : "REVIEW NEEDED"),
      escapeCsv("Proposal Lead"),
      escapeCsv(r.sourcePage || ""),
    ].join(",");
  });

  return [headers.join(","), ...formRows, ...ruleRows].join("\r\n");
}

export interface ExecutiveBriefContentParams {
  opportunity: Opportunity;
  assessment?: StrategicAssessment | null;
  requirements: Requirement[];
  milestones: ProposalMilestone[];
  addenda: ProposalAddendum[];
  forms: RequiredForm[];
  formatRules: FormatRule[];
  sectionGroups: ProposalSectionGroup[];
}

/**
 * Generates clean, executive HTML representation suitable for print, PDF generation, or Google Docs
 */
export function generateExecutiveBriefHtml(params: ExecutiveBriefContentParams): string {
  const {
    opportunity,
    assessment,
    requirements,
    milestones,
    addenda,
    forms,
    formatRules,
    sectionGroups,
  } = params;

  const mandatoryCount = requirements.filter((r) => r.mandatory).length;
  const confirmedMandatory = requirements.filter((r) => r.mandatory && r.confirmed).length;
  const pendingForms = forms.filter(
    (f) => f.mandatory && f.status !== "attached" && f.status !== "signed",
  ).length;
  const pendingAddenda = addenda.filter(
    (a) => a.acknowledgmentRequired && a.acknowledgmentStatus !== "acknowledged",
  ).length;

  const brief = assessment?.executiveBrief;

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Executive Opportunity Brief - ${opportunity.title}</title>
  <style>
    @page { size: letter; margin: 0.75in; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1e293b; line-height: 1.5; font-size: 10pt; margin: 0; padding: 24px; }
    h1 { font-size: 20pt; color: #0f172a; margin: 0 0 6px 0; font-weight: 700; }
    h2 { font-size: 13pt; color: #0f172a; border-bottom: 2px solid #2ba5b5; padding-bottom: 4px; margin: 24px 0 12px 0; text-transform: uppercase; letter-spacing: 0.05em; }
    h3 { font-size: 11pt; color: #334155; margin: 12px 0 4px 0; font-weight: 600; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 8.5pt; font-weight: 600; }
    .badge-teal { background: #e0f2f1; color: #00796b; }
    .badge-amber { background: #fef3c7; color: #b45309; }
    .badge-slate { background: #f1f5f9; color: #475569; }
    .meta-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; background: #f8fafc; border: 1px solid #e2e8f0; padding: 12px; border-radius: 8px; }
    .meta-item strong { display: block; font-size: 8pt; color: #64748b; text-transform: uppercase; margin-bottom: 2px; }
    .meta-item span { font-size: 10.5pt; font-weight: 600; color: #0f172a; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 9pt; }
    th { background: #f1f5f9; border: 1px solid #cbd5e1; padding: 6px 10px; text-align: left; font-weight: 600; color: #334155; }
    td { border: 1px solid #e2e8f0; padding: 6px 10px; color: #1e293b; vertical-align: top; }
    .callout { background: #f0fdfa; border-left: 4px solid #2ba5b5; padding: 10px 14px; border-radius: 0 6px 6px 0; margin-bottom: 16px; }
    .risk-card { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 8px 12px; margin-bottom: 8px; font-size: 8.5pt; }
    @media print {
      body { padding: 0; }
      .no-print { display: none !important; }
      .page-break { page-break-before: always; }
    }
  </style>
</head>
<body>
  <!-- Header -->
  <div style="border-bottom: 3px solid #2ba5b5; padding-bottom: 12px; margin-bottom: 16px;">
    <div style="display: flex; justify-content: space-between; align-items: flex-start;">
      <div>
        <span class="badge badge-teal">MLC PROPOSAL RESPONSE PLAN</span>
        ${opportunity.planFrozen ? '<span class="badge badge-amber" style="margin-left: 6px;">LOCKED BASELINE</span>' : ""}
        <h1>${opportunity.title}</h1>
        <div style="font-size: 11pt; color: #475569;">
          Client: <strong>${opportunity.client}</strong> &bull; Lead: <strong>${opportunity.owner}</strong>
        </div>
      </div>
      <div style="text-align: right;">
        <div style="font-size: 14pt; font-weight: 700; color: #2ba5b5;">MULTILINGUAL CONNECTIONS</div>
        <div style="font-size: 8.5pt; color: #64748b;">Certified Women's Business Enterprise (WBE)</div>
      </div>
    </div>
  </div>

  <!-- Meta Summary Grid -->
  <div class="meta-grid">
    <div class="meta-item">
      <strong>Submission Deadline</strong>
      <span>${opportunity.dueDate || "Not specified"}${opportunity.dueTime ? ` @ ${opportunity.dueTime}` : ""}</span>
    </div>
    <div class="meta-item">
      <strong>Current Stage</strong>
      <span>${opportunity.stage.replace(/_/g, " ").toUpperCase()}</span>
    </div>
    <div class="meta-item">
      <strong>Core Services</strong>
      <span>${opportunity.services && opportunity.services.length > 0 ? opportunity.services.join(", ") : "Translation / Localization"}</span>
    </div>
    <div class="meta-item">
      <strong>Go/No-Go Decision</strong>
      <span>${opportunity.decision === "go" ? "APPROVED GO" : opportunity.decision || "UNDECIDED"}</span>
    </div>
  </div>

  <!-- Win Themes & Strategy -->
  <h2>1. Strategic Fit & Win Strategy</h2>
  <div class="callout">
    <div style="font-weight: 700; color: #00796b; font-size: 10.5pt; margin-bottom: 4px;">
      Primary Win Theme: ${brief?.recommendedWinTheme || "Tailored Multilingual Excellence with Certified WBE Agility"}
    </div>
    <div style="color: #334155; font-size: 9.5pt;">
      ${brief?.winStrategySummary || "Emphasize responsive account management, rigorously vetted linguist networks, proven public sector compliance, and ISO-aligned quality assurance processes."}
    </div>
  </div>

  ${
    brief?.keyDifferentiators && brief.keyDifferentiators.length > 0
      ? `<h3>Key MLC Differentiators</h3>
         <ul>
           ${brief.keyDifferentiators.map((d) => `<li>${d}</li>`).join("")}
         </ul>`
      : ""
  }

  <!-- Section Ownership Matrix -->
  <h2>2. Bid Section Ownership & Outlines</h2>
  <table>
    <thead>
      <tr>
        <th style="width: 15%;">Section Ref</th>
        <th style="width: 35%;">Section Title</th>
        <th style="width: 20%;">Primary Drafter</th>
        <th style="width: 15%;">SME Reviewer</th>
        <th style="width: 15%;">Target Words</th>
      </tr>
    </thead>
    <tbody>
      ${
        sectionGroups.length === 0
          ? '<tr><td colspan="5" style="text-align: center; color: #64748b; font-style: italic;">No outline sections populated.</td></tr>'
          : sectionGroups
              .map(
                (sec) => `<tr>
            <td><strong>${sec.sectionRef}</strong></td>
            <td>${sec.title}</td>
            <td>${sec.assignedTo || '<span style="color: #b45309;">Unassigned</span>'}</td>
            <td>${sec.reviewerName || "Unassigned"}</td>
            <td>${sec.targetWordCount > 0 ? `${sec.targetWordCount.toLocaleString()} words` : "-"}</td>
          </tr>`,
              )
              .join("")
      }
    </tbody>
  </table>

  <!-- Response Schedule Milestones -->
  <h2>3. Key Schedule & Gate Milestones</h2>
  <table>
    <thead>
      <tr>
        <th style="width: 25%;">Milestone</th>
        <th style="width: 20%;">Category</th>
        <th style="width: 20%;">Target Date</th>
        <th style="width: 20%;">Owner</th>
        <th style="width: 15%;">Status</th>
      </tr>
    </thead>
    <tbody>
      ${
        milestones.length === 0
          ? '<tr><td colspan="5" style="text-align: center; color: #64748b; font-style: italic;">No milestones scheduled.</td></tr>'
          : milestones
              .map(
                (m) => `<tr>
            <td><strong>${m.title}</strong></td>
            <td>${m.category.replace(/_/g, " ")}</td>
            <td>${m.targetDate}${m.targetTime ? ` ${m.targetTime}` : ""}</td>
            <td>${m.ownerName || m.ownerEmail || "Team"}</td>
            <td>${m.status.toUpperCase()}</td>
          </tr>`,
              )
              .join("")
      }
    </tbody>
  </table>

  <!-- Compliance Scorecard -->
  <h2>4. Compliance & Submission Scorecard</h2>
  <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 10px;">
    <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px; text-align: center;">
      <div style="font-size: 16pt; font-weight: 700; color: #0f172a;">${confirmedMandatory}/${mandatoryCount}</div>
      <div style="font-size: 8pt; color: #64748b; text-transform: uppercase;">Mandatory Reqs Confirmed</div>
    </div>
    <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px; text-align: center;">
      <div style="font-size: 16pt; font-weight: 700; color: ${pendingAddenda === 0 ? "#059669" : "#d97706"};">${addenda.length - pendingAddenda}/${addenda.length}</div>
      <div style="font-size: 8pt; color: #64748b; text-transform: uppercase;">Addenda Acknowledged</div>
    </div>
    <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px; text-align: center;">
      <div style="font-size: 16pt; font-weight: 700; color: ${pendingForms === 0 ? "#059669" : "#d97706"};">${forms.length - pendingForms}/${forms.length}</div>
      <div style="font-size: 8pt; color: #64748b; text-transform: uppercase;">Mandatory Forms Ready</div>
    </div>
    <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px; text-align: center;">
      <div style="font-size: 16pt; font-weight: 700; color: #0f172a;">${formatRules.length}</div>
      <div style="font-size: 8pt; color: #64748b; text-transform: uppercase;">Format Rules Monitored</div>
    </div>
  </div>

  <div style="margin-top: 32px; padding-top: 12px; border-top: 1px solid #cbd5e1; font-size: 8pt; color: #94a3b8; display: flex; justify-content: space-between;">
    <span>Generated by MLC Proposal HQ &bull; Confidential Proposal Material</span>
    <span>Date: ${new Date().toLocaleDateString()}</span>
  </div>
</body>
</html>`;

  return html;
}

/**
 * Generates Markdown representation of the Executive Opportunity Brief
 */
export function generateExecutiveBriefMarkdown(params: ExecutiveBriefContentParams): string {
  const {
    opportunity,
    assessment,
    requirements,
    milestones,
    addenda,
    forms,
    formatRules,
    sectionGroups,
  } = params;

  const mandatoryCount = requirements.filter((r) => r.mandatory).length;
  const confirmedMandatory = requirements.filter((r) => r.mandatory && r.confirmed).length;
  const brief = assessment?.executiveBrief;

  let md = `# Executive Opportunity Brief: ${opportunity.title}\n\n`;
  md += `**Client:** ${opportunity.client}\n`;
  md += `**Proposal Lead:** ${opportunity.owner}\n`;
  md += `**Submission Deadline:** ${opportunity.dueDate || "Not specified"}${opportunity.dueTime ? ` @ ${opportunity.dueTime}` : ""}\n`;
  md += `**Status:** ${opportunity.stage.toUpperCase()}${opportunity.planFrozen ? " (LOCKED BASELINE)" : ""}\n\n`;

  md += `## 1. Win Strategy & Themes\n\n`;
  md += `> **Primary Win Theme:** ${brief?.recommendedWinTheme || "Tailored Multilingual Excellence with Certified WBE Agility"}\n\n`;
  md += `${brief?.winStrategySummary || "Emphasize responsive account management, rigorously vetted linguist networks, proven public sector compliance, and ISO-aligned quality assurance processes."}\n\n`;

  if (brief?.keyDifferentiators && brief.keyDifferentiators.length > 0) {
    md += `### Key Differentiators\n\n`;
    brief.keyDifferentiators.forEach((d) => {
      md += `- ${d}\n`;
    });
    md += `\n`;
  }

  md += `## 2. Section Ownership Outline\n\n`;
  md += `| Section Ref | Section Title | Drafter | SME Reviewer | Target Words |\n`;
  md += `| :--- | :--- | :--- | :--- | :--- |\n`;
  sectionGroups.forEach((sec) => {
    md += `| ${sec.sectionRef} | ${sec.title} | ${sec.assignedTo || "Unassigned"} | ${sec.reviewerName || "Unassigned"} | ${sec.targetWordCount > 0 ? sec.targetWordCount : "-"} |\n`;
  });
  md += `\n`;

  md += `## 3. Response Schedule Milestones\n\n`;
  md += `| Milestone | Category | Due Date | Owner | Status |\n`;
  md += `| :--- | :--- | :--- | :--- | :--- |\n`;
  milestones.forEach((m) => {
    md += `| ${m.title} | ${m.category} | ${m.targetDate} | ${m.ownerName || m.ownerEmail || "Team"} | ${m.status.toUpperCase()} |\n`;
  });
  md += `\n`;

  md += `## 4. Compliance Scorecard\n\n`;
  md += `- Mandatory Requirements Confirmed: **${confirmedMandatory} / ${mandatoryCount}**\n`;
  md += `- Total Requirements in Matrix: **${requirements.length}**\n`;
  md += `- Client Addenda Logged: **${addenda.length}**\n`;
  md += `- Required Forms: **${forms.length}**\n`;
  md += `- Format Rules: **${formatRules.length}**\n\n`;

  return md;
}

/**
 * Triggers a browser download of a text or CSV string
 */
export function downloadTextFile(
  filename: string,
  content: string,
  mimeType = "text/csv;charset=utf-8;",
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
